export type {Assistance,MethodKind,MethodReference,ConstructionConfig} from './graph-pedagogy';
import {equationRoots} from './graph-equation';
import type {XY} from './graph-geometry';

// A scene stores definitions, never cached answers. References form a directed graph.
export type Scalar = number | {parameter:string} | {object:string; component:'x'|'y'|'value'};
type Base = {id:string; label:string};
export type CoordinateDisplay='none'|'x'|'y'|'both';
export type Primitive = Base & (
 | {kind:'point'; x:Scalar; y:Scalar}
 | {kind:'onCurve'; curve:string; x:Scalar}
 | {kind:'crosshair';constraint:'free'|'curve'|'vertical'|'horizontal';x:Scalar;y:Scalar;curve?:string;showPoint?:boolean;movable?:boolean;coordinates?:CoordinateDisplay;correctionCoordinates?:CoordinateDisplay;precision?:'auto'|number}
 | {kind:'horizontal'|'vertical'; value:Scalar}
 | {kind:'line'; a:string; b:string}
 | {kind:'intersection'; a:string; b:string; branch:number}
 | {kind:'projection'; point:string; axis:'x'|'y'; at:Scalar}
 | {kind:'interval'; a:string; b:string; axis:'x'|'y'}
 | {kind:'level'; start:Scalar; end:Scalar; fraction:Scalar}
 | {kind:'tangent'; curve:string; point:string; slope?:number}
);
export type Geometry = {kind:'point'; point:XY; from?:XY} | {kind:'line'; a:XY; b:XY} | {kind:'value'; value:number; a?:XY; b?:XY; axis?:'x'|'y'};
export type Source = {kind:'curve'; at:(x:number)=>number} | Extract<Geometry,{kind:'line'}>;
export type ConstructionContext = {sources:Record<string,Source>; parameters:Record<string,number>; min:number; max:number};
export type Resolution = {geometry?:Geometry; error?:string};
export const primitiveLabels:Record<Primitive['kind'],string>={point:'Point libre',onCurve:'Point sur courbe',crosshair:'Réticule / curseur',horizontal:'Horizontale',vertical:'Verticale',line:'Droite par deux points',intersection:'Intersection',projection:'Projection',interval:'Intervalle Δx / Δy',level:'Niveau relatif',tangent:'Tangente'};
const fail=(message:string):never=>{throw Error(message);};
const finite=(n:number)=>Number.isFinite(n)?n:fail('Valeur non définie.');
export const scalarValid=(s:unknown):boolean=>typeof s==='number'?Number.isFinite(s):!!s&&typeof s==='object'&&('parameter' in s?typeof s.parameter==='string':'object' in s&&typeof s.object==='string'&&'component' in s&&['x','y','value'].includes(String(s.component)));
export function validPrimitives(input:unknown):input is Primitive[]{
 if(!Array.isArray(input)||input.length>100)return false;
 const ids=new Set<string>();
 return input.every(p=>{
  if(!p||typeof p.id!=='string'||!p.id||['theory','model','line'].includes(p.id)||ids.has(p.id)||typeof p.label!=='string'||p.label.length>100)return false;ids.add(p.id);
  const ref=(k:string)=>typeof p[k]==='string'&&!!p[k];
  switch(p.kind){
   case 'point':return scalarValid(p.x)&&scalarValid(p.y);
   case 'onCurve':return ref('curve')&&scalarValid(p.x);
   case 'crosshair':return (p.precision===undefined||p.precision==='auto'||Number.isInteger(p.precision)&&p.precision>=0&&p.precision<=12)&&['free','curve','vertical','horizontal'].includes(p.constraint)&&scalarValid(p.x)&&scalarValid(p.y)&&(p.constraint!=='curve'||ref('curve'))&&['showPoint','movable'].every(k=>p[k]===undefined||typeof p[k]==='boolean')&&['coordinates','correctionCoordinates'].every(k=>p[k]===undefined||['none','x','y','both'].includes(p[k]));
   case 'horizontal':case 'vertical':return scalarValid(p.value);
   case 'line':return ref('a')&&ref('b');
   case 'intersection':return ref('a')&&ref('b')&&Number.isInteger(p.branch)&&p.branch>=0;
   case 'projection':return ref('point')&&['x','y'].includes(p.axis)&&scalarValid(p.at);
   case 'interval':return ref('a')&&ref('b')&&['x','y'].includes(p.axis);
   case 'level':return [p.start,p.end,p.fraction].every(scalarValid);
   case 'tangent':return ref('curve')&&ref('point')&&(p.slope===undefined||Number.isFinite(p.slope));
   default:return false;
  }
 });
}
export function dependencies(p:Primitive):string[]{
 const refs:string[]=[];for(const [key,value] of Object.entries(p)){
  if(p.kind==='crosshair'&&(key==='curve'&&p.constraint!=='curve'||key==='y'&&p.constraint==='curve'))continue;
  if(['a','b','curve','point'].includes(key)&&typeof value==='string')refs.push(value);
  if(value&&typeof value==='object'&&'object' in value)refs.push(value.object);
 }return [...new Set(refs)];
}
export function removeConstruction(objects:Primitive[],id:string):Primitive[]{
 const removed=new Set([id]);let changed=true;
 while(changed){changed=false;for(const p of objects)if(!removed.has(p.id)&&dependencies(p).some(d=>removed.has(d))){removed.add(p.id);changed=true;}}
 return objects.filter(p=>!removed.has(p.id));
}
export function constructionOrder(objects:Primitive[]):Primitive[]{
 const seen=new Set<string>(),result:Primitive[]=[],byId=new Map(objects.map(p=>[p.id,p]));
 const visit=(p:Primitive)=>{if(seen.has(p.id))return;seen.add(p.id);for(const id of dependencies(p)){const dependency=byId.get(id);if(dependency)visit(dependency);}result.push(p);};
 objects.forEach(visit);return result;
}
function intersections(a:Source,b:Source,min:number,max:number):XY[]{
 if(a.kind==='line'&&b.kind==='line'){
  const u={x:a.b.x-a.a.x,y:a.b.y-a.a.y},v={x:b.b.x-b.a.x,y:b.b.y-b.a.y};
  const un=Math.hypot(u.x,u.y),vn=Math.hypot(v.x,v.y);if(!un||!vn)fail('Droite non définie.');
  const det=(u.x/un)*(v.y/vn)-(u.y/un)*(v.x/vn);
  if(det===0)fail('Droites parallèles ou confondues : aucune intersection unique.');
  const t=((b.a.x-a.a.x)*(v.y/vn)-(b.a.y-a.a.y)*(v.x/vn))/det;
  return [{x:a.a.x+t*u.x/un,y:a.a.y+t*u.y/un}];
 }
 const vertical=[a,b].find(s=>s.kind==='line'&&s.a.x===s.b.x);
 if(vertical?.kind==='line'){
  const other=a===vertical?b:a;const x=vertical.a.x;
  return [{x,y:sourceValue(other,x)}];
 }
 const result=equationRoots(x=>{try{return sourceValue(a,x)-sourceValue(b,x);}catch{return NaN;}},0,min,max);
 if(result.infinite)fail('Courbes confondues sur cet intervalle.');
 return result.xs.map(x=>({x,y:sourceValue(a,x)}));
}
function sourceValue(s:Source,x:number):number{
 if(s.kind==='curve')return finite(s.at(x));
 const dx=s.b.x-s.a.x;if(!dx)fail('Une verticale ne définit pas une ordonnée unique.');
 return finite(s.a.y+(x-s.a.x)*(s.b.y-s.a.y)/dx);
}
// Local derivative used only to construct a tangent; no derivative-curve tool.
function tangentSlope(source:Source,x:number,span:number):number {
 if(source.kind==='line')return finite((source.b.y-source.a.y)/(source.b.x-source.a.x));
 const h=Math.max(Math.abs(span)*1e-5,Math.abs(x)*1e-7,1e-12),y=sourceValue(source,x);
 const l=source.at(x-h),r=source.at(x+h);
 if(!Number.isFinite(l))return finite((-3*y+4*source.at(x+h)-source.at(x+2*h))/(2*h));
 if(!Number.isFinite(r))return finite((3*y-4*source.at(x-h)+source.at(x-2*h))/(2*h));
 const left=(y-l)/h,right=(r-y)/h;
 const halfLeft=(y-source.at(x-h/2))/(h/2),halfRight=(source.at(x+h/2)-y)/(h/2);
 if(Math.abs(halfLeft-halfRight)>.75*Math.abs(left-right)&&Math.abs(left-right)>1e-3*Math.max(1e-12,Math.abs(left),Math.abs(right)))fail('Tangente non définie ou courbe insuffisamment régulière.');
 return finite((r-l)/(2*h));
}
export function resolveConstructions(objects:Primitive[],context:ConstructionContext):Record<string,Resolution>{
 const results:Record<string,Resolution>=Object.create(null),visiting=new Set<string>();
 const byId=new Map(objects.map(p=>[p.id,p]));
 const get=(id:string):Geometry=>{
  if(results[id])return results[id].geometry??fail(results[id].error!);
  if(visiting.has(id))return fail('Dépendance circulaire.');
  const p=byId.get(id);if(!p)return fail('Objet manquant : '+id);
  visiting.add(id);
  try{const g=calculate(p);results[id]={geometry:g};return g;}
  catch(e){const error=e instanceof Error?e.message:'Construction invalide.';results[id]={error};return fail(error);}
  finally{visiting.delete(id);}
 };
 const scalar=(s:Scalar):number=>{
  if(typeof s==='number')return finite(s);
  if('parameter' in s)return finite(context.parameters[s.parameter]);
  const g=get(s.object);return finite(s.component==='value'?(g.kind==='value'?g.value:NaN):g.kind==='point'?g.point[s.component]:NaN);
 };
 const point=(id:string)=>{const g=get(id);return g.kind==='point'?g.point:fail('Un point est nécessaire.');};
 const source=(id:string):Source=>{
  if(Object.hasOwn(context.sources,id))return context.sources[id];
  const g=get(id);return g.kind==='line'?g:fail('Une droite ou une courbe est nécessaire.');
 };
 const calculate=(p:Primitive):Geometry=>{
  switch(p.kind){
   case 'point':return {kind:'point',point:{x:scalar(p.x),y:scalar(p.y)}};
   case 'onCurve':{const x=scalar(p.x);return {kind:'point',point:{x,y:sourceValue(source(p.curve),x)}};}
   case 'crosshair':{const x=scalar(p.x);return {kind:'point',point:{x,y:p.constraint==='curve'?sourceValue(source(p.curve!),x):scalar(p.y)}};}
   case 'horizontal':{const y=scalar(p.value);return {kind:'line',a:{x:context.min,y},b:{x:context.max,y}};}
   case 'vertical':{const x=scalar(p.value);return {kind:'line',a:{x,y:0},b:{x,y:1}};}
   case 'line':{const a=point(p.a),b=point(p.b);if(a.x===b.x&&a.y===b.y)fail('Deux points distincts sont nécessaires.');return {kind:'line',a,b};}
   case 'level':return {kind:'value',value:finite(scalar(p.start)+scalar(p.fraction)*(scalar(p.end)-scalar(p.start)))};
   case 'intersection':{const ps=intersections(source(p.a),source(p.b),context.min,context.max);const q=ps[p.branch];if(!q||!Number.isFinite(q.x)||!Number.isFinite(q.y))fail('Intersection absente sur la branche choisie.');return {kind:'point',point:q};}
   case 'projection':{const from=point(p.point);return {kind:'point',from,point:p.axis==='x'?{x:from.x,y:scalar(p.at)}:{x:scalar(p.at),y:from.y}};}
   case 'interval':{const a=point(p.a),b=point(p.b);return {kind:'value',a,b,axis:p.axis,value:finite(b[p.axis]-a[p.axis])};}
   case 'tangent':{const a=point(p.point),s=source(p.curve),y=sourceValue(s,a.x);if(Math.abs(a.y-y)>1e-8*Math.max(1,Math.abs(y)))fail('Le point de tangence doit appartenir à la courbe.');const slope=p.slope??tangentSlope(s,a.x,context.max-context.min),dx=(context.max-context.min)/10;return {kind:'line',a,b:{x:a.x+dx,y:finite(a.y+slope*dx)}};}
  }
 };
 for(const p of objects)try{get(p.id);}catch{/* Keep each invalid object visible in the inspector. */}
 return results;
}
